import { ref } from 'vue'
import { filterValidLensTechDisplay } from '~/assets/js/helpers/search/search'
import { useReviewsStore } from '~/stores/reviews'

export default {
  render() {
    return this.$slots.default({
      products: this.formattedProducts,
      tag: this.tag,
    })
  },

  async setup(props) {
    const formattedProducts = ref([])
    const { $i18n } = useNuxtApp()

    async function setFormattedProductsFromBaseProduct() {
      const { $product } = useNuxtApp()

      // Get customised product from base product SKU
      const baseProduct = await $product.getCustomisedProduct(
        props.baseProductSku
      )

      if (!baseProduct.heroDesigns?.length) {
        formattedProducts.value = [getDataFromCustomisedProduct(baseProduct)]
      } else {
        formattedProducts.value = await Promise.all(
          baseProduct.heroDesigns
            .filter(
              (heroDesign) => !heroDesign.isLimitedEdition // Don't include limited editions
            )
            .map(async (heroDesign, index) => {
              if (
                props.variantQuantityLimit &&
                props.variantQuantityLimit <= index
              )
                return

              const customisedProduct = await $product.getCustomisedProduct(
                props.baseProductSku,
                {
                  sku: heroDesign.sku,
                }
              )

              if (props.usePrescriptionPricing) {
                const firstRXLens = customisedProduct.prescriptionSunglassesLenses[0]
                if (firstRXLens) customisedProduct.setChoice(firstRXLens.sku)
              }

              return {
                ...getDataFromCustomisedProduct(customisedProduct),
                theme: '',
              }
            })
        ).then((res) => res.filter((productVariant) => productVariant))
      }
    }

    async function setFormattedProducts() {
      const { $product } = useNuxtApp()

      formattedProducts.value = await Promise.all(
        props.products.map(async (customisedProductData, index) => {
          if (props.variantQuantityLimit && props.variantQuantityLimit <= index)
            return null

          const customisedProduct = await $product.getCustomisedProduct(
            customisedProductData.baseProduct.sku,
            customisedProductData
          )

          if (props.usePrescriptionPricing) {
            const firstRXLens = customisedProduct.prescriptionSunglassesLenses[0]
            if (firstRXLens) customisedProduct.setChoice(firstRXLens.sku)
          }

          const { subTitle = '', theme } = customisedProductData

          return {
            ...getDataFromCustomisedProduct(customisedProduct),
            subTitle,
            theme,
            logo:
              customisedProduct.product.limitedEditionLogo ||
              customisedProductData.logo,
          }
        })
      ).then((res) => res.filter((productVariant) => productVariant))
    }

    function getDataFromCustomisedProduct(customisedProduct) {
      const showMinPrice = customisedProduct.showMinimumPrice
      const compareAtPrice = showMinPrice
        ? customisedProduct.minimumCompareAtPrice
        : customisedProduct.compareAtPrice
        
      return {
        subTitle: '',
        title: customisedProduct.baseName,
        titleSuffix: customisedProduct.frameTypeDetails?.title,
        productInfoTitle: customisedProduct.product.productInfoTitle,
        price: customisedProduct.price,
        compareAtPrice,
        baseName: customisedProduct.baseName,
        baseSKU: customisedProduct.baseSKU,
        showMinPrice,
        minPrice: customisedProduct.minimumPrice,
        swatches: customisedProduct.swatches,
        swatchIcon: customisedProduct.swatchIconOverride?.url,
        isIris: customisedProduct.isIrisLens,
        isRecycled: customisedProduct.isRecycled,
        isLimitedEdition: customisedProduct.isLimitedEdition,
        url: customisedProduct.url,
        sku: customisedProduct.sku,
        normalisedSKU: customisedProduct.normalisedSKU,
        imageSKU: customisedProduct.legacySKU,
        lensPartName: customisedProduct.lensName,
        tag: {
          text: customisedProduct.product.tagTextWithOverride || props.tag.text,
          theme: props.tag.text ? 'default' : customisedProduct.product.tagTheme,
        },
        promoTag: {
          text: customisedProduct.product.promoMessaging?.tagText,
          theme: customisedProduct.product.promoMessaging?.theme?.themeType
        },
        inStock: customisedProduct.isInStock,
        onPreorder: false,
        preorderDate: null,
        delayedDespatchDate: customisedProduct.delayedDespatchDate,
        logo: customisedProduct.product.limitedEditionLogo,
        imgConfig: props.img,
        showReviews: customisedProduct.product.showReviews,
        useGenericReviews: customisedProduct.product.useGenericReviews,
        lensTech: {
          displayValue: customisedProduct.lensChoice?.optionTech?.displayTitle,
          filterValue: customisedProduct.lensChoice?.lensTech,
        },
        lensColour: customisedProduct.lensChoice?.nameWithoutLensTech,
        frameColour: customisedProduct.frameChoice?.name,
        reviewSummary: reviewSummary(customisedProduct),
      }
    }

    if (props.useBaseProduct) {
      await setFormattedProductsFromBaseProduct()
    } else {
      await setFormattedProducts()
    }

    function reviewSummary(item) {
      const reviewStore = useReviewsStore()

      if (item.product.useGenericReviews) {
        return reviewStore.aggregated
      }

      return item.product?.reviewSummary?.[0]
    }

    function calculatedDiscountedPrice(price) {
      if (!props.priceOverride?.percentageOff) {
        return 0
      }

      const discountedPrice = Math.round(
        (price * (100 - props.priceOverride.percentageOff)) / 100
      )

      if (discountedPrice <= 0 || discountedPrice >= price) return 0

      return discountedPrice
    }

    function buildItemPayload(item, index) {
      return {
        sku: item.sku,
        name: item.title,
        nameSuffix: item.titleSuffix,
        productInfoTitle: item.productInfoTitle,
        inStock: item.inStock,
        onPreorder: item.onPreorder,
        showOutOfStock: true,
        url: item.url,
        legacySKU: item.imageSKU,
        prices: {
          [$i18n.localeProperties.value.currency]: item.price
        },
        discountedPrices: {
          [$i18n.localeProperties.value.currency]: calculatedDiscountedPrice(
            item.price
          )
        },
        logo: item.logo,
        swatchIcon: { url: item.swatchIcon },
        lensTech: filterValidLensTechDisplay(item.lensTech),
        lensColour: item.lensColour,
        frameColour: item.frameColour,
        isLimitedEdition: item.isLimitedEdition || false,
        reviews: item.reviewSummary,
        swatchStyles: item.swatches,
        tag: { text: item.tagText, theme: item.tagTheme },
        promoTag: item.promoTag,
        index,
      }
    }

    function transformItem(items) {
      const mainVariant = buildItemPayload(items[0], 0)

      mainVariant.variants = items.map((item, index) => {
        return buildItemPayload(item, index + 1)
      })

      return mainVariant
    }

    if (props.lite) {
      formattedProducts.value = transformItem(formattedProducts.value)
    }

    if (props.showGiftCard) {
      formattedProducts.value.variants.push({
        ...formattedProducts.value.variants[0],
        static: true,
        sku: 'static',
        inStock: true,
        productInfoTitle: 'Customisable gift card value',
        tag: { tagText: '' },
        staticImage: {
          url: 'https://www.datocms-assets.com/45158/1699005413-giftcard22.png?auto=format&w=600',
          alt: 'SunGod E-Gift Card',
        },
        swatchIcon: {
          url: 'data:image/png;base64,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',
        },
        url: '#giftcard',
        name: 'E-Gift Card',
        event: {
          name: 'click-giftcard',
        },
        index: formattedProducts.value.variants.length + 1,
      })
    }

    return { formattedProducts }
  },

  props: {
    tag: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    useBaseProduct: {
      type: Boolean,
      default: false,
    },
    baseProductSku: {
      type: String,
      default: null,
    },
    products: {
      type: Array,
      default: () => [],
      validator: (value) => {
        if (!value.length) return true
        return value.every(
          (product) => product.baseProduct && product.baseProduct.sku
        )
      },
    },
    variantQuantityLimit: {
      type: Number,
      default: null,
    },
    img: {
      type: Object,
      require: false,
    },
    usePrescriptionPricing: {
      type: Boolean,
      default: false,
    },
    showGiftCard: {
      type: Boolean,
      default: false,
    },
    lite: {
      type: Boolean,
      default: false,
    },
    priceOverride: {
      type: Object,
      default: () => ({}),
    },
  },
}
